import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Chip,
  Avatar,
  Grid,
  Paper,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  CardGiftcard as GiftIcon,
  Phone as PhoneIcon,
  TableChart as TabletIcon,
  Watch as WatchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as ActiveIcon,
  EmojiEvents as RewardIcon,
} from '@mui/icons-material';
import { rewardService } from '../../services/reward';

const HistoryReward = () => {
  const [associates, setAssociates] = useState([]);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewTabValue, setViewTabValue] = useState(0);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch (error) {
      return 'N/A';
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return 'N/A';
    }
  };

  const mapApiDataToAssociates = (apiData) => {
    // Group by associate
    const associateMap = new Map();

    if (!Array.isArray(apiData)) {
      console.warn('API data is not an array:', apiData);
      return [];
    }

    apiData.forEach((item) => {
      // Handle both old and new API response structures
      const associate = item.associate || item.associateId || {};
      const reward = item.reward || item.rewardId || {};
      const associateId = associate._id || associate.associateId;

      if (!associateId) {
        console.warn('Skipping item with no associate ID:', item);
        return;
      }

      if (!associateMap.has(associateId)) {
        associateMap.set(associateId, {
          id: associateId,
          associateId: associate.associateId ? `ASSOC-${associate.associateId}` : associate._id || 'N/A',
          associateName: associate.name || 'N/A',
          phone: associate.phone || 'N/A',
          email: associate.email || 'N/A',
          city: '', // Not in API response
          state: '', // Not in API response
          totalPoints: associate.rewardPoints || 0,
          totalRedemptions: 0,
          totalPointsRedeemed: 0,
          status: 'Active', // Default status
          redemptions: [],
        });
      }

      const associateData = associateMap.get(associateId);
      const redemption = {
        id: item._id || item.requestId,
        rewardId: reward.rewardId ? `RWD-${reward.rewardId}` : reward._id || 'N/A',
        rewardName: reward.title || 'N/A',
        categoryName: 'General', // Not in API response, using default
        categoryCode: 'RWD-CAT-GEN',
        keywords: [], // Not in API response
        pointsUsed: reward.redeemPoints || 0,
        redemptionDate: formatDate(item.createdAt),
        redemptionTime: formatTime(item.createdAt),
        status: item.requestStatus === 'approved' ? 'Approved' : 
                item.requestStatus === 'rejected' ? 'Rejected' : 'Pending',
      };

      associateData.redemptions.push(redemption);
      associateData.totalRedemptions += 1;
      associateData.totalPointsRedeemed += redemption.pointsUsed;
    });

    return Array.from(associateMap.values());
  };

  // const mapApiDataToAssociates = (apiRequest) => {
  //   const associate = apiRequest.associate || apiRequest.associateId || {};
  //   const reward = apiRequest.reward || apiRequest.rewardId || {};
    
  //   return {
  //     id: apiRequest._id || apiRequest.id,
  //     requestId: apiRequest.requestId ? `REQ-${apiRequest.requestId}` : 'N/A',
  //     customerId: associate.associateId ? `ASSOC-${associate.associateId}` : associate._id || 'N/A',
  //     customerName: associate.name || 'N/A',
  //     customerEmail: associate.email || 'N/A',
  //     customerPhone: associate.phone || 'N/A',
  //     rewardId: reward.rewardId ? `RWD-${reward.rewardId}` : reward._id || 'N/A',
  //     rewardName: reward.title || 'N/A',
  //     pointsRedeemed: reward.redeemPoints || 0,
  //     requestDate: apiRequest.createdAt || new Date().toISOString(),
  //     status: apiRequest.requestStatus === 'pending' ? 'Pending' : 
  //             apiRequest.requestStatus === 'approved' ? 'Approved' : 
  //             apiRequest.requestStatus === 'rejected' ? 'Rejected' : 'Pending',
  //     remarks: apiRequest.remarks || '',
  //     associateData: associate,
  //     rewardData: reward,
  //   };
  // };

  const fetchHistoryRewards = async () => {
    try {
      setLoading(true);
      // Fetch all processed requests (approved and rejected)
      const response = await rewardService.getRedeemRequests('processed', 1);
      // Handle both response structures: { data: [...], extra: {...} } or just array
      const apiData = Array.isArray(response) ? response : (response.data || []);
      const mappedAssociates = mapApiDataToAssociates(apiData);
      setAssociates(mappedAssociates);
    } catch (err) {
      console.error('Failed to fetch reward history:', err);
      setAssociates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistoryRewards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(0);
  };

  const filteredAssociates = associates.filter((associate) => {
    const matchesSearch =
      associate.associateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      associate.associateId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      associate.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      associate.phone.includes(searchTerm);
    
    // Filter by redemption status if status filter is set
    // If 'All', show all associates. Otherwise, show associates that have at least one redemption with matching status
    const matchesStatus = statusFilter === 'All' || 
      associate.redemptions.some((redemption) => redemption.status === statusFilter);
    
    return matchesSearch && matchesStatus;
  });

  const displayData = filteredAssociates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleView = (associate) => {
    setSelectedAssociate(associate);
    setViewDialogOpen(true);
    setViewTabValue(0);
  };

  const getAvatarColor = (name) => {
    const colors = ['#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2', '#00acc1'];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const getAvatarInitials = (name) => {
    const words = name.split(' ');
    if (words.length === 1) {
      return name.substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  const getCategoryColor = (categoryName) => {
    const colors = {
      Gadgets: '#9c27b0',
      Travel: '#2e7d32',
      Fan: '#ff9800',
      Bike: '#1976d2',
    };
    return colors[categoryName] || '#757575';
  };

  // Calculate statistics
  const totalAssociates = associates.length;
  const activeAssociates = associates.filter((a) => a.status === 'Active').length;
  const totalRedemptions = associates.reduce((sum, a) => sum + a.totalRedemptions, 0);
  const totalPointsRedeemed = associates.reduce((sum, a) => sum + a.totalPointsRedeemed, 0);
  const totalPointsAvailable = associates.reduce((sum, a) => sum + (a.totalPoints - a.totalPointsRedeemed), 0);

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Reward History
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View associate reward redemption history and track redemptions
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchHistoryRewards}
            disabled={loading}
            sx={{
              fontWeight: 600,
              px: 3,
              py: 1.2,
              textTransform: 'none',
              fontSize: '15px',
              borderRadius: 2,
              borderColor: '#536DFE',
              color: '#536DFE',
              '&:hover': {
                borderColor: '#536DFE',
                backgroundColor: '#f0f7ff',
              },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      {/* <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderLeft: '4px solid #1976d2',
              background: 'linear-gradient(135deg, #e3f2fd 0%, #f0f7ff 100%)',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(25, 118, 210, 0.2)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Total Associates
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#1976d2' }}>
                    {totalAssociates}
                  </Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 48, color: '#1976d2', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderLeft: '4px solid #2e7d32',
              background: 'linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(46, 125, 50, 0.2)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Active Associates
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#2e7d32' }}>
                    {activeAssociates}
                  </Typography>
                </Box>
                <ActiveIcon sx={{ fontSize: 48, color: '#2e7d32', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderLeft: '4px solid #ff9800',
              background: 'linear-gradient(135deg, #fff3e0 0%, #fef7e0 100%)',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(255, 152, 0, 0.2)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Total Redemptions
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#ff9800' }}>
                    {totalRedemptions}
                  </Typography>
                </Box>
                <GiftIcon sx={{ fontSize: 48, color: '#ff9800', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              borderLeft: '4px solid #9c27b0',
              background: 'linear-gradient(135deg, #f3e5f5 0%, #fce4ec 100%)',
              '&:hover': {
                boxShadow: '0 4px 16px rgba(156, 39, 176, 0.2)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    Points Redeemed
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" sx={{ color: '#9c27b0' }}>
                    {totalPointsRedeemed.toLocaleString()}
                  </Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, color: '#9c27b0', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid> */}

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Associate Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, ID, email, or phone..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
                  <MenuItem value="All">All Status</MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('All');
                }}
                sx={{
                  height: '40px',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderColor: '#536DFE',
                  color: '#536DFE',
                  '&:hover': {
                    borderColor: '#536DFE',
                    backgroundColor: '#f0f7ff',
                  },
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Associates Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Associate List
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, p: 4 }}>
              <CircularProgress size={50} />
            </Box>
          ) : (
            <TableContainer>
              <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Associate</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Contact</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Total Points</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Redemptions</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Points Redeemed</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }} align="center">
                    Status
                  </TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayData.length > 0 ? (
                  displayData.map((associate) => (
                    <TableRow
                      key={associate.id}
                      hover
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                        '&:hover': { backgroundColor: '#f0f7ff !important' },
                      }}
                    >
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.5}>
                          <Avatar
                            sx={{
                              bgcolor: getAvatarColor(associate.associateName),
                              width: 40,
                              height: 40,
                              fontSize: '0.875rem',
                              fontWeight: 700,
                            }}
                          >
                            {getAvatarInitials(associate.associateName)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '14px' }}>
                              {associate.associateName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {associate.associateId}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {associate.phone}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {associate.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                          {associate.totalPoints.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Available: {(associate.totalPoints - associate.totalPointsRedeemed).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {associate.totalRedemptions}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Total rewards
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#9c27b0' }}>
                          {associate.totalPointsRedeemed.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Points used
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={associate.status}
                          size="small"
                          color={associate.status === 'Active' ? 'success' : 'default'}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Reward History">
                          <IconButton
                            color="info"
                            size="small"
                            onClick={() => handleView(associate)}
                            sx={{ border: '1px solid #e0e0e0' }}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No associates found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}

          {/* Pagination */}
          {!loading && filteredAssociates.length > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {page * rowsPerPage + 1} to {Math.min((page + 1) * rowsPerPage, filteredAssociates.length)} of{' '}
                {filteredAssociates.length} associates
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  sx={{ textTransform: 'none' }}
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  onClick={() => setPage(Math.min(Math.ceil(filteredAssociates.length / rowsPerPage) - 1, page + 1))}
                  disabled={page >= Math.ceil(filteredAssociates.length / rowsPerPage) - 1}
                  sx={{ textTransform: 'none' }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* View Associate Reward History Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: '#536DFE',
            color: 'white',
            py: 2.5,
            px: 3,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            Reward History - {selectedAssociate?.associateName}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          {selectedAssociate && (
            <Box>
              <Tabs
                value={viewTabValue}
                onChange={(e, newValue) => setViewTabValue(newValue)}
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
              >
                <Tab label="Associate Info" icon={<PersonIcon />} iconPosition="start" />
                <Tab label="Reward History" icon={<HistoryIcon />} iconPosition="start" />
              </Tabs>

              {/* Associate Info Tab */}
              {viewTabValue === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} display="flex" justifyContent="center" mb={2}>
                    <Avatar
                      sx={{
                        bgcolor: getAvatarColor(selectedAssociate.associateName),
                        width: 100,
                        height: 100,
                        fontSize: '2.5rem',
                        fontWeight: 700,
                      }}
                    >
                      {getAvatarInitials(selectedAssociate.associateName)}
                    </Avatar>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Associate ID
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedAssociate.associateId}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Associate Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedAssociate.associateName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Phone
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedAssociate.phone}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Email
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedAssociate.email}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Location
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {selectedAssociate.city}, {selectedAssociate.state}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Status
                    </Typography>
                    <Chip
                      label={selectedAssociate.status}
                      size="small"
                      color={selectedAssociate.status === 'Active' ? 'success' : 'default'}
                      sx={{ fontWeight: 600, mt: 0.5 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Total Points
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                      {selectedAssociate.totalPoints.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Points Redeemed
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#9c27b0' }}>
                      {selectedAssociate.totalPointsRedeemed.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Available Points
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                      {(selectedAssociate.totalPoints - selectedAssociate.totalPointsRedeemed).toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      Total Redemptions
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#ff9800' }}>
                      {selectedAssociate.totalRedemptions}
                    </Typography>
                  </Grid>
                </Grid>
              )}

              {/* Reward History Tab */}
              {viewTabValue === 1 && (
                <Box>
                  {selectedAssociate.redemptions && selectedAssociate.redemptions.length > 0 ? (
                    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#536DFE' }}>
                            <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Reward</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Points Used</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Date & Time</TableCell>
                            <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedAssociate.redemptions.map((redemption) => (
                            <TableRow
                              key={redemption.id}
                              hover
                              sx={{
                                '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                                '&:hover': { backgroundColor: '#f0f7ff !important' },
                              }}
                            >
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {redemption.rewardName}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {redemption.rewardId}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#9c27b0' }}>
                                  {redemption.pointsUsed.toLocaleString()}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {redemption.redemptionDate}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {redemption.redemptionTime}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={redemption.status}
                                  size="small"
                                  color={redemption.status === 'Approved' ? 'success' : redemption.status === 'Rejected' ? 'error' : 'warning'}
                                  sx={{ fontWeight: 600 }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        No reward redemptions found
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
          <Button onClick={() => setViewDialogOpen(false)} variant="outlined" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HistoryReward;

